<template>
  <div class="page-margin query-history-page">
    <b-container fluid>
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <h1 class="mb-2">История поиска</h1>
        <b-form-group label="Записей на странице" label-for="query-history-page-size" class="mb-2">
          <b-form-select id="query-history-page-size"
                         v-model="pageSize"
                         :options="pageSizeOptions"
                         :disabled="loading"
                         @change="changePageSize"/>
        </b-form-group>
      </div>

      <b-alert :show="loadFailed" variant="danger">
        <div class="d-flex justify-content-between align-items-center">
          <span>Не удалось загрузить историю поиска.</span>
          <b-button size="sm" variant="outline-danger" @click="loadHistory">Повторить</b-button>
        </div>
      </b-alert>

      <b-table :items="items"
               :fields="fields"
               :busy="loading"
               :responsive="true"
               :show-empty="true"
               primary-key="id"
               bordered
               hover
               striped>
        <template #table-busy>
          <div class="text-center text-primary my-4">
            <b-spinner class="align-middle"/>
            <strong class="ml-2">Загрузка...</strong>
          </div>
        </template>

        <template #empty>
          <div class="text-center text-muted my-4">История поиска пуста</div>
        </template>

        <template #cell(eventTime)="data">
          <span class="text-nowrap">{{ formatDate(data.value) }}</span>
        </template>

        <template #cell(customerId)="data">
          {{ displayValue(data.value) }}
        </template>

        <template #cell(cardNumber)="data">
          {{ displayValue(data.value) }}
        </template>

        <template #cell(query)="data">
          <span class="query-text">{{ displayValue(data.value) }}</span>
        </template>

        <template #cell(categoryIds)="data">
          <span v-if="data.value && data.value.length">{{ data.value.join(", ") }}</span>
          <span v-else>—</span>
        </template>

        <template #cell(sort)="data">
          {{ displayValue(data.value) }}
        </template>

        <template #cell(totalHits)="data">
          {{ displayValue(data.value) }}
        </template>

        <template #cell(tookMs)="data">
          <span v-if="data.value !== null && data.value !== undefined">{{ data.value }} мс</span>
          <span v-else>—</span>
        </template>

        <template #cell(details)="row">
          <b-button size="sm"
                    variant="outline-primary"
                    :aria-label="row.detailsShowing ? 'Скрыть подробности' : 'Показать подробности'"
                    @click="row.toggleDetails">
            <b-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"/>
          </b-button>
        </template>

        <template #row-details="row">
          <b-card class="details-card">
            <b-row class="mb-3">
              <b-col md="4"><strong>Версия приложения:</strong> {{ displayValue(row.item.appVersion) }}</b-col>
              <b-col md="4"><strong>ID сессии:</strong> {{ displayValue(row.item.sessionId) }}</b-col>
              <b-col md="4"><strong>ID записи:</strong> {{ displayValue(row.item.id) }}</b-col>
            </b-row>

            <h6>Первые результаты поиска</h6>
            <b-table :items="row.item.topItems || []"
                     :fields="topItemFields"
                     :show-empty="true"
                     small
                     bordered
                     responsive>
              <template #empty>
                <div class="text-center text-muted">Нет данных</div>
              </template>
              <template #cell(price)="data">
                {{ formatPrice(data.value) }}
              </template>
              <template #cell(inStock)="data">
                {{ formatStock(data.value) }}
              </template>
              <template #cell(score)="data">
                {{ formatScore(data.value) }}
              </template>
            </b-table>
          </b-card>
        </template>
      </b-table>

      <div class="d-flex justify-content-center align-items-center mt-3 mb-4">
        <b-button variant="outline-primary"
                  :disabled="loading || pageNumber === 1"
                  @click="previousPage">
          <b-icon icon="chevron-left"/>
          Назад
        </b-button>
        <span class="mx-3">Страница {{ pageNumber }}</span>
        <b-button variant="outline-primary"
                  :disabled="loading || nextPageNumber === null"
                  @click="nextPage">
          Вперёд
          <b-icon icon="chevron-right"/>
        </b-button>
      </div>
    </b-container>
  </div>
</template>

<script>
import moment from "moment";
import queryHistoryService from "@/modules/query-history/api/service/query-history-service";

export default {
  name: "query-history",
  data() {
    return {
      fields: [
        {key: "eventTime", label: "Дата и время"},
        {key: "customerId", label: "ID пользователя"},
        {key: "cardNumber", label: "Номер карты"},
        {key: "query", label: "Запрос"},
        {key: "categoryIds", label: "Категории"},
        {key: "sort", label: "Сортировка"},
        {key: "totalHits", label: "Найдено"},
        {key: "tookMs", label: "Время поиска"},
        {key: "details", label: "", class: "details-column"}
      ],
      topItemFields: [
        {key: "id", label: "ID товара"},
        {key: "title", label: "Название"},
        {key: "price", label: "Цена"},
        {key: "inStock", label: "В наличии"},
        {key: "score", label: "Релевантность"}
      ],
      items: [],
      pageNumber: 1,
      pageSize: 20,
      pageSizeOptions: [10, 20, 50],
      nextPageNumber: null,
      loading: false,
      loadFailed: false
    };
  },
  created() {
    document.title = this.$route.meta.title;
    this.loadHistory();
  },
  methods: {
    loadHistory() {
      this.loading = true;
      this.loadFailed = false;

      return queryHistoryService.getAll(this.pageNumber, this.pageSize)
          .then(response => {
            const data = response.data || {};
            this.items = Array.isArray(data.items) ? data.items : [];
            this.nextPageNumber = data.nextPageNumber === undefined ? null : data.nextPageNumber;
          })
          .catch(() => {
            this.items = [];
            this.nextPageNumber = null;
            this.loadFailed = true;
          })
          .finally(() => {
            this.loading = false;
          });
    },
    previousPage() {
      if (this.pageNumber > 1) {
        this.pageNumber -= 1;
        this.loadHistory();
      }
    },
    nextPage() {
      if (this.nextPageNumber !== null) {
        this.pageNumber = this.nextPageNumber;
        this.loadHistory();
      }
    },
    changePageSize() {
      this.pageNumber = 1;
      this.loadHistory();
    },
    displayValue(value) {
      return value === null || value === undefined || value === "" ? "—" : value;
    },
    formatDate(value) {
      return value ? moment(value).format("DD.MM.YYYY HH:mm:ss") : "—";
    },
    formatPrice(value) {
      return value === null || value === undefined ? "—" : value;
    },
    formatStock(value) {
      if (value === null || value === undefined) {
        return "—";
      }
      return value ? "Да" : "Нет";
    },
    formatScore(value) {
      return value === null || value === undefined ? "—" : Number(value).toFixed(3);
    }
  }
};
</script>

<style scoped>
.query-history-page {
  padding: 2rem;
}

.query-text {
  min-width: 12rem;
  display: inline-block;
}

.details-card {
  background-color: #f8f9fa;
}

::v-deep .details-column {
  width: 3.5rem;
  text-align: center;
}
</style>
